import { Text as MantineText } from "@mantine/core";

import { colors } from "#app/theme/v2/colors";
import { fontSizes } from "#app/theme/v2/fontSizes";
import { fontWeights } from "#app/theme/v2/fontWeights";
import { Tooltip } from "#components/atoms/Tooltip/Tooltip";
import { useIsTruncated } from "#shared/hooks/useIsTruncated";

type TextProps = {
  children: React.ReactNode;
  size?: "base" | "small" | "tiny";
  type?: "bold" | "medium" | "regular" | "link";
  color?: string;
  textAlign?: "left" | "center" | "right";
  truncate?: boolean;
  style?: React.CSSProperties;
  component?: "p" | "span";
};

const fontWeightsMap = {
  bold: fontWeights.bold,
  medium: fontWeights.medium,
  regular: fontWeights.regular,
  link: fontWeights.medium,
};

const fontSizesMap = {
  base: fontSizes[16],
  small: fontSizes[14],
  tiny: fontSizes[12],
};

const lineHeightsMap = {
  base: fontSizes[24],
  small: fontSizes[20],
  tiny: fontSizes[16],
};

export { lineHeightsMap as TEXT_LINE_HEIGHTS };

const letterSpacingMap = {
  bold: { base: "-0.6%", small: "-0.2%", tiny: "0.4%" },
  medium: { base: "-0.3%", small: "0%", tiny: "0.8%" },
  regular: { base: "0%", small: "0.4%", tiny: "1.2%" },
  link: { base: "-1.5%", small: "0.4%", tiny: "-1%" },
};

export { letterSpacingMap as TEXT_LETTER_SPACING };

const textDecorationMap = {
  bold: "none",
  medium: "none",
  regular: "none",
  link: "underline",
};

const Text = ({
  children,
  size = "base",
  type = "regular",
  color,
  textAlign,
  truncate,
  style,
  component = "p",
}: TextProps) => {
  const { ref, isTruncated } = useIsTruncated<HTMLParagraphElement>();

  const textElement = (
    <MantineText
      component={component}
      ref={truncate ? ref : undefined}
      fw={fontWeightsMap[type]}
      fz={fontSizesMap[size]}
      lh={lineHeightsMap[size]}
      lts={letterSpacingMap[type][size]}
      td={textDecorationMap[type]}
      c={color ?? colors.text.primaryDefault}
      ta={textAlign}
      truncate={truncate}
      style={style}
    >
      {children}
    </MantineText>
  );

  if (!truncate) {
    return textElement;
  }

  return (
    <Tooltip label={children} disabled={!isTruncated} wrap={false}>
      {textElement}
    </Tooltip>
  );
};

export { Text };
